import { motion } from 'framer-motion';
import { X, Check, BookOpen, Clock, Gift, Download, FileText } from 'lucide-react';
import type { Chapter, Material } from '../types';

interface ChapterNavProps {
  chapters: Chapter[];
  currentChapter: number;
  onSelectChapter: (index: number) => void;
  onClose: () => void;
  theme: 'light' | 'dark' | 'sepia';
  materials?: Material[];
  onDownloadMaterial?: (material: Material) => void;
}

export default function ChapterNav({
  chapters,
  currentChapter,
  onSelectChapter,
  onClose,
  theme,
  materials = [],
  onDownloadMaterial,
}: ChapterNavProps) {
  const completedCount = chapters.filter(ch => ch.isCompleted).length;
  const progress = Math.round((completedCount / chapters.length) * 100);

  const isDark = theme === 'dark';

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 100,
        }}
      />

      {/* Sidebar */}
      <motion.aside
        initial={{ x: -320 }}
        animate={{ x: 0 }}
        exit={{ x: -320 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: '320px',
          maxWidth: '85vw',
          background: isDark ? '#1a1a2e' : '#ffffff',
          zIndex: 101,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '4px 0 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <BookOpen size={20} color="var(--accent)" />
            <span
              style={{
                fontWeight: 600,
                color: isDark ? '#fff' : 'var(--text)',
                fontSize: '1rem',
              }}
            >
              Chapters
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '0.5rem',
              cursor: 'pointer',
              color: isDark ? 'rgba(255,255,255,0.7)' : 'var(--text-light)',
              display: 'flex',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Overview */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--off-white)',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)'}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.75rem',
            }}
          >
            <span
              style={{
                fontSize: '0.875rem',
                color: isDark ? 'rgba(255,255,255,0.7)' : 'var(--text-light)',
              }}
            >
              Your Progress
            </span>
            <span
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--accent)',
              }}
            >
              {completedCount} of {chapters.length}
            </span>
          </div>
          <div
            style={{
              height: '8px',
              background: isDark ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
              style={{
                height: '100%',
                background: progress === 100 ? 'var(--success)' : 'var(--accent)',
                borderRadius: '4px',
              }}
            />
          </div>
          <p
            style={{
              fontSize: '0.8125rem',
              color: isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-light)',
              marginTop: '0.5rem',
              textAlign: 'center',
            }}
          >
            {progress === 100
              ? 'Book completed!'
              : `${progress}% complete`}
          </p>
        </div>

        {/* Chapter List */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '1rem 0',
          }}
        >
          {chapters.map((chapter, index) => {
            const isCurrent = index === currentChapter;
            const isCompleted = chapter.isCompleted;

            return (
              <motion.button
                key={chapter.id}
                onClick={() => onSelectChapter(index)}
                whileHover={{ x: 4 }}
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  background: isCurrent
                    ? isDark
                      ? 'rgba(220, 38, 38, 0.15)'
                      : 'var(--accent-light)'
                    : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  borderLeft: isCurrent ? '3px solid var(--accent)' : '3px solid transparent',
                  transition: 'all 0.2s',
                }}
              >
                {/* Chapter Number / Status */}
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: isCompleted
                      ? 'var(--success)'
                      : isCurrent
                      ? 'var(--accent)'
                      : isDark
                      ? 'rgba(255,255,255,0.1)'
                      : 'var(--light-gray)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: isCompleted || isCurrent ? 'white' : isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-light)',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                  }}
                >
                  {isCompleted ? <Check size={16} /> : chapter.number}
                </div>

                {/* Chapter Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: isCurrent ? 600 : 500,
                      color: isCurrent
                        ? 'var(--accent)'
                        : isDark
                        ? '#fff'
                        : 'var(--text)',
                      fontSize: '0.9375rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {chapter.title}
                  </p>
                  <p
                    style={{
                      margin: '0.25rem 0 0',
                      fontSize: '0.8125rem',
                      color: isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-light)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                    }}
                  >
                    <Clock size={12} />
                    {chapter.readingTime}
                    {isCompleted && (
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          color: 'var(--success)',
                          fontWeight: 500,
                        }}
                      >
                        Completed
                      </span>
                    )}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Bonus Materials */}
        {materials.length > 0 && (
          <div
            style={{
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)'}`,
            }}
          >
            <div
              style={{
                padding: '1rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--off-white)',
              }}
            >
              <Gift size={18} color="var(--accent)" />
              <span
                style={{
                  fontWeight: 600,
                  color: isDark ? '#fff' : 'var(--text)',
                  fontSize: '0.9375rem',
                }}
              >
                Bonus Materials
              </span>
              <span
                style={{
                  fontSize: '0.75rem',
                  background: isDark ? 'rgba(255,255,255,0.1)' : '#e0e0e0',
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'var(--text-light)',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '4px',
                }}
              >
                {materials.length}
              </span>
            </div>
            <div style={{ padding: '0.5rem 0' }}>
              {materials.map((material) => (
                <motion.button
                  key={material.id}
                  onClick={() => onDownloadMaterial?.(material)}
                  whileHover={{ x: 4 }}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.5rem',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.875rem',
                    transition: 'all 0.2s',
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: isDark ? 'rgba(220, 38, 38, 0.15)' : '#fee2e2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--accent)',
                      flexShrink: 0,
                    }}
                  >
                    <FileText size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        margin: 0,
                        fontWeight: 500,
                        color: isDark ? '#fff' : 'var(--text)',
                        fontSize: '0.875rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {material.name}
                    </p>
                    <p
                      style={{
                        margin: '0.125rem 0 0',
                        fontSize: '0.75rem',
                        color: isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-light)',
                      }}
                    >
                      {material.fileSize || 'PDF'}
                    </p>
                  </div>
                  <Download
                    size={16}
                    style={{
                      color: isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-light)',
                      flexShrink: 0,
                    }}
                  />
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)'}`,
            background: isDark ? 'rgba(255,255,255,0.02)' : 'var(--off-white)',
            marginTop: 'auto',
          }}
        >
          <p
            style={{
              fontSize: '0.8125rem',
              color: isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-light)',
              textAlign: 'center',
              margin: 0,
            }}
          >
            Use arrow keys to navigate chapters
          </p>
        </div>
      </motion.aside>
    </>
  );
}
